<template>
  <v-dialog
    v-model="dialog"
    width="768"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        v-bind="attrs"
        v-on="on"
        :text="textBtn"
        rounded
      >
        <v-icon v-if="icon" left>
          {{ icon }}
        </v-icon>
        <span v-if="methodTitle">{{ methodTitle }}</span>
      </v-btn>
    </template>

    <v-card>
      <v-alert
        v-if="method === 'edit'"
        prominent
        :type="editWarningType"
        tile
      >
        <v-row align="center">
          <v-col class="grow">
            {{ editWarningText }}
          </v-col>
          <v-col class="shrink">
            <v-checkbox 
              v-model="ignoreInUpload"
              label="Update Manually"
              class="pr-5"
            ></v-checkbox>
          </v-col>
        </v-row>
      </v-alert>
      <v-card-title>
        <v-row class="d-flex justify-space-between align-center">
          <v-col class="grow">
            <span class="headline">
              {{ methodTitle }}
              <span v-if="employee" class="headline">
                {{ employee.fields.Name }}
              </span>
            </span>
          </v-col>
          <v-col v-if="method === 'edit'" class="d-flex justify-end shrink">
            <v-switch
              v-model="isActive"
              label="Active"
              prepend-icon="mdi-flash-circle"
              required
            >Active</v-switch>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row>
            <v-col>

            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field
                v-model="name"
                label="Full Name"
                prepend-icon="mdi-text-short"
                required
              ></v-text-field>
            </v-col>
            <v-col>
              <v-text-field
                v-model="email"
                label="Email"
                prepend-icon="mdi-email"
                hint="Changing this manually might have unintended consequences such as logging in or direct reports"
                required
                autocomplete="new-password"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-text-field
                v-model="title"
                label="Title"
                prepend-icon="mdi-briefcase"
                required
              ></v-text-field>
            </v-col>
            <v-col>
              <v-autocomplete
                v-model="department"
                :items="departmentOptions"
                prepend-icon="mdi-sitemap"
                label="Department"
                required
                autocomplete="new-password"
              ></v-autocomplete>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-autocomplete
                v-model="location"
                :items="locationOptions"
                prepend-icon="mdi-map-marker"
                label="Location"
                autocomplete="new-password"
                required
              ></v-autocomplete>
            </v-col>
            <v-col>
              <v-select
                :items="['Male', 'Female']"
                v-model="gender"
                label="Gender"
                prepend-icon="mdi-account"
                required
              ></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-menu
                ref="hireDateMenu"
                v-model="hireDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="hireDate"
                    label="Hire Date"
                    prepend-icon="event"
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker 
                  v-model="hireDate" 
                  no-title 
                  @input="hireDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col>
              <v-autocomplete
                v-model="managerEmail"
                :items="managerEmailOptions"
                label="Manager Email"
                prepend-icon="mdi-account-supervisor"
                required
                autocomplete="new-password"
              ></v-autocomplete>
            </v-col>
          </v-row>
          <v-row class="d-flex justify-space-between">
            <v-col cols="8">
              <v-btn
                v-if="showDeleteButton"
                color="error"
                rounded
                text
                :disabled="!enableSaveButton"
                @click="toggleShowDeleteConfirmation"
              >
                Delete Employee
              </v-btn>
              <v-btn 
                v-if="showDeleteConfirmationButton"
                color="error"
                rounded
                class="mr-5"
                @click="deleteEmployee"
                :loading="deleteLoading"
              >
                Really Delete Employee?
              </v-btn>
              <v-btn 
                v-if="showDeleteConfirmationButton"
                text
                rounded
                @click="toggleShowDeleteConfirmation"
              >
                Nevermind
              </v-btn>
            </v-col>
            <v-col class="d-flex justify-end">
              <v-btn 
                text
                :disabled="saveLoading"
                @click="close"
                rounded
              >Cancel</v-btn>
              <v-btn 
                color="primary"
                text
                rounded
                :disabled="!enableSaveButton"
                :loading="saveLoading"
                @click="save"
              >Save</v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
  export default {
    name: 'EmployeeForm',
    props: {
      method: String,
      employee: Object,
      methodTitle: String,
      icon: String,
      textBtn: Boolean
    },
    data () {
      return {
        name: '',
        email: '',
        title: '',
        department: '',
        location: '',
        gender: '',
        hireDate: '',
        isActive: true,
        hireDateMenu: false,
        managerEmail: '',
        ignoreInUpload: false,
        saveLoading: false,
        dialog: false,
        showSuccessMsg: false,
        successMsg: '',
        showDeleteConfirmationButton: false,
        deleteLoading: false,
      }
    },
    computed: {
      employees () {
        return this.$store.getters.getEmployees
      },
      editWarningType () {
        if (this.showDeleteConfirmationButton) {
          return 'error'
        } else if (this.ignoreInUpload) {
          return 'info'
        } else {
          return 'warning'
        }
      },
      editWarningText () {
        if (this.showDeleteConfirmationButton) {
          return `Deleting an employee will remove them from all trainings. If this employee left Group Nine, you should Inactivate them instead. You can't undo this so it's a pretty big deal.`
        } else if (this.ignoreInUpload) {
          return 'Employees who are updated manually will not have their information updated automatically with uploads. That means you need to change their title, department, manager, active status, etc. if it ever changes.'
        } else {
          return 'Edits to an employee will be overwritten in the next upload unless you choose to check "Update Manually". If they are not included in the next upload, they will be marked as Inactive.'
        }
      },
      showDeleteButton () {
        return !this.showDeleteConfirmationButton && this.method === 'edit'
      },
      enableSaveButton () {
        return this.name  !== '' &&
               this.email !== '' &&
               this.title !== '' &&
               this.department   !== '' &&
               this.location     !== '' &&
               this.gender       !== '' &&
               this.hireDate     !== '' &&
               this.managerEmail !== '' &&
               !this.saveLoading
      },
      departmentOptions () {
        const depts = this.employees.filter(e => {
          return e.fields.Status !== 'Inactive' && e.fields.Department
        }).map(e => {
          return e.fields.Department
        }).sort((a, b) => {
          return a > b ? 1 : -1
        })
        // vuetify should already remove dupes
        // but we had a console error with this recently
        const uniqueDepts = new Set(depts)
        return [...uniqueDepts]
      },
      locationOptions () {
        const locs = this.employees.filter(e => {
          return e.fields.Status !== 'Inactive' && e.fields.Location
        }).map(e => {
          return e.fields.Location
        }).sort((a, b) => {
          return a > b ? 1 : -1
        })
        // vuetify should already remove dupes
        // but we had a console error with this recently
        const uniqueLocs = new Set(locs)
        return [...uniqueLocs]
      },
      managerEmailOptions () {
        return this.employees.filter(e => {
          return e.fields.Status !== 'Inacvtive'
        }).map(e => {
          return e.fields.Email.toLowerCase()
        }).sort((a, b) => {
          return a > b ? 1 : -1
        })
      }
    },
    created () {
      this.init()
    },
    watch: {
      employee (newVal, oldVal) {
        if (newVal && oldVal && newVal.id !== oldVal.id) {
          this.init()
        }
      }
    },
    methods: {
      init () {
        if (this.employee) {
          this.name = this.employee.fields.Name
          this.email = this.employee.fields.Email
          this.title = this.employee.fields.Title
          this.department = this.employee.fields.Department
          this.location = this.employee.fields.Location
          this.gender = this.employee.fields.Gender
          this.hireDate = this.employee.fields['Hire Date']
          this.managerEmail = this.employee.fields['Manager Email']
          this.ignoreInUpload = this.employee.fields['Ignore In Upload']
          this.isActive = this.employee.fields.Status === 'Active'
        }
        this.$store.dispatch('getRecords', {
          table: 'Employees',
          view: 'All Employees',
          dispatch: 'setEmployees'
        })
      },
      save () {
        this.saveLoading = true
        const fields = {
          'Preferred Name': this.name,
          'Email': this.email,
          'Title': this.title,
          'Department': this.department,
          'Location': this.location,
          'Gender': this.gender,
          'Hire Date': this.hireDate,
          'Manager Email': this.managerEmail,
          'Status': this.isActive ? 'Active' : 'Inactive',
          'Ignore In Upload': this.method === 'create' ? 
                                              true : this.ignoreInUpload
        }
        if (this.method === 'create') {
          this.$store.dispatch('createRecords', {
            table: 'Employees',
            records: [{ fields: fields }]
          })
          .then((values) => {
            this.$store.dispatch('addEmployee', values[0].data[0])
            this.$store.dispatch('setSnackbar', {
              snackbarColor: 'success',
              showSnackbar: true,
              snackbarY: 'top',
              snackbarX: 'right',
              snackbarText: `Created ${this.name}`
            })
            this.saveLoading = false
            this.close()
          })
        } else if (this.method === 'edit') {
          this.$store.dispatch('updateRecords', {
            table: 'Employees',
            records: [{ id: this.employee.id, fields: fields }]
          })
          .then(() => {
            this.$store.dispatch('getRecords', {
              table: 'Employees',
              view: 'All Employees',
              dispatch: 'setEmployees'
            })
            .then(() => {
              this.$store.dispatch('setSnackbar', {
                snackbarColor: 'success',
                showSnackbar: true,
                snackbarY: 'top',
                snackbarX: 'right',
                snackbarText: `Updated ${this.name}`
              })
              this.saveLoading = false
              this.close()
            })
          })
        }
      },
      deleteEmployee () {
        this.deleteLoading = true
        this.$store.dispatch('deleteRecords', {
          table: 'Employees',
          records: [this.employee]
        })
        .then(() => {
          this.showDeleteConfirmationButton = false
          this.deleteLoading = false
          this.$store.dispatch('setSnackbar', {
            snackbarColor: 'success',
            showSnackbar: true,
            snackbarY: 'top',
            snackbarX: 'right',
            snackbarText: `Deleted ${this.employee.fields.Name}`
          })
          this.$router.push({ name: 'Employee Directory' })
        })
      },
      close () {
        this.dialog = false
        this.showDeleteConfirmationButton = false
      },
      clearForm () {
        this.name = ''
        this.email = ''
        this.title = ''
        this.department = ''
        this.location = ''
        this.gender = ''
        this.hireDate = ''
        this.managerEmail = ''
      },
      toggleShowDeleteConfirmation () {
        return this.showDeleteConfirmationButton = 
               !this.showDeleteConfirmationButton
      }
    }
  }
</script>
